import { useCallback, useContext, useMemo } from 'react'
import { ConfigContext } from '../store/ConfigContext'
import OneTwoThreeClient from '../clients/OneTwoThreeClient'
import { SwlsDriversClient } from '../clients/SwlsDriversClient'
import { openSkuSoftware } from '../utils'
import { FeatureFlagKeys } from '../store/Constants'

/**
 * Easy Start download logic.
 * - If SKU is present: reuse cached 123 software URL for that SKU or fetch again.
 * - If no SKU: fallback to OS-based SWLS flow (Windows/Mac only) via getDriverDownloadUrlByOS.
 */
export default function useEasyStartDownload({
  setLoading = () => {},
  forceByOS = false
} = {}) {
  const { stack, getStoreData, sku, os, flags } = useContext(ConfigContext)

  const client123 = useMemo(() => new OneTwoThreeClient(stack), [stack])
  const swlsClient = useMemo(() => new SwlsDriversClient(), [])

  const installEasyStart = useCallback(async () => {
    try {
      setLoading(true)
      if (sku && !forceByOS) {
        const migrationFlag = flags?.[FeatureFlagKeys.migration123]
        const opened = await openSkuSoftware({
          sku,
          getStoreData,
          client123,
          swlsClient,
          os,
          flags,
          migrationFlag
        })
        if (!opened) {
          console.error('EasyStart: Driver URL not found for SKU:', sku)
        }
      } else {
        const driverUrl = await swlsClient.getDriverDownloadUrlByOS(os)
        if (driverUrl) {
          window.location.href = driverUrl
        }
      }
    } catch (error) {
      console.error('EasyStart: Error fetching drivers:', error)
    } finally {
      setLoading(false)
    }
  }, [
    sku,
    os,
    client123,
    swlsClient,
    setLoading,
    getStoreData,
    flags,
    forceByOS
  ])

  return { installEasyStart }
}
